import React, { useEffect, useState, useRef, createElement } from 'react';
import { Ticket, X, User, Mail, Phone, Globe, ArrowRight, Map, Compass, Layers, Target, ShieldCheck, Clock, Sparkles, CheckCircle2, CircleX, CircleDot, Quote, Lightbulb, Workflow, Megaphone, FileText, Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Footer } from '../components/Footer';
declare global {
  interface Window {
    fbq: any;
  }
}
const COUNTRY_CODES = [{
  code: '+1',
  country: 'USA/Canada'
}, {
  code: '+52',
  country: 'México'
}, {
  code: '+57',
  country: 'Colombia'
}, {
  code: '+34',
  country: 'España'
}, {
  code: '+54',
  country: 'Argentina'
}, {
  code: '+56',
  country: 'Chile'
}, {
  code: '+51',
  country: 'Perú'
}, {
  code: '+593',
  country: 'Ecuador'
}, {
  code: '+58',
  country: 'Venezuela'
}, {
  code: '+502',
  country: 'Guatemala'
}, {
  code: '+503',
  country: 'El Salvador'
}, {
  code: '+504',
  country: 'Honduras'
}, {
  code: '+505',
  country: 'Nicaragua'
}, {
  code: '+506',
  country: 'Costa Rica'
}, {
  code: '+507',
  country: 'Panamá'
}, {
  code: '+591',
  country: 'Bolivia'
}, {
  code: '+595',
  country: 'Paraguay'
}, {
  code: '+598',
  country: 'Uruguay'
}, {
  code: '+1-809',
  country: 'Rep. Dominicana'
}];
const WEBHOOK_URL = 'https://services.leadconnectorhq.com/hooks/HMYR0XQcpZnIeoWD2nrn/webhook-trigger/9e77fbea-dfb7-4800-9398-1f4c8701198d';
const MIRROR_ROWS = [{
  think: '"No sé qué vender."',
  real: 'No tienes un sistema para encontrar una idea simple y vendible.'
}, {
  think: '"No tengo experiencia."',
  real: 'Estás creyendo que necesitas ser experto antes de empezar.'
}, {
  think: '"No tengo seguidores."',
  real: 'Estás confundiendo audiencia con oferta y tráfico.'
}, {
  think: '"No soy bueno con tecnología."',
  real: 'Nadie te ha dado los prompts y pasos correctos.'
}, {
  think: '"Siempre empiezo y lo dejo."',
  real: 'No eres inconsistente; has estado tratando de construir sin mapa.'
}];
const PROMISES = [{
  icon: Lightbulb,
  title: 'Cómo encontrar una idea de producto digital con AI',
  why: 'Para dejar de perder tiempo pensando "qué vendo".'
}, {
  icon: Sparkles,
  title: 'Cómo usar AI para crear el contenido del producto',
  why: 'Para avanzar más rápido aunque tengas poco tiempo después del trabajo.'
}, {
  icon: Target,
  title: 'Cómo estructurar tu oferta',
  why: 'Para que no vendas información suelta, sino una solución clara.'
}, {
  icon: FileText,
  title: 'Cómo crear una página simple de venta',
  why: 'Para presentar tu producto de forma profesional.'
}, {
  icon: Megaphone,
  title: 'Cómo preparar los primeros assets para Meta Ads',
  why: 'Para no depender de seguidores ni de publicar al azar.'
}];
const PHASES = [{
  phase: 'Fase 1',
  title: 'Encuentras una idea simple de producto digital.',
  icon: Search
}, {
  phase: 'Fase 2',
  title: 'Usas AI para crear la estructura y contenido del producto.',
  icon: Sparkles
}, {
  phase: 'Fase 3',
  title: 'Conviertes el producto en una oferta clara.',
  icon: Target
}, {
  phase: 'Fase 4',
  title: 'Creas la página y los mensajes de venta.',
  icon: FileText
}, {
  phase: 'Fase 5',
  title: 'Preparas los assets para empezar a atraer compradores.',
  icon: Megaphone
}];
const OBJECTIONS = [{
  o: '"No sé qué producto crear."',
  r: 'En el entrenamiento verás cómo usar AI para encontrar y estructurar una idea simple.'
}, {
  o: '"No tengo seguidores."',
  r: 'El enfoque no depende de ser influencer; se enfoca en oferta, página y assets de venta.'
}, {
  o: '"No soy experto."',
  r: 'La meta es ayudarte a empaquetar una solución clara, no convertirte en gurú de la noche a la mañana.'
}, {
  o: '"No tengo mucho tiempo."',
  r: 'Por eso el sistema está pensado como una ruta de 48 horas, no como meses de teoría.'
}, {
  o: '"Ya intenté cosas antes."',
  r: 'Probablemente intentaste tácticas sueltas, no una secuencia completa.'
}];
const BEFORE_AFTER = [{
  before: 'Tienes ideas sueltas.',
  after: 'Tienes una ruta clara.'
}, {
  before: 'Guardas videos de AI.',
  after: 'Sabes cómo usar AI para construir.'
}, {
  before: 'No sabes qué vender.',
  after: 'Entiendes cómo encontrar una idea vendible.'
}, {
  before: 'Crees que necesitas seguidores.',
  after: 'Ves cómo una oferta y una página cambian el juego.'
}, {
  before: 'Te sientes atrás.',
  after: 'Empiezas a verte como alguien que puede ejecutar.'
}];
const getNextThursday = () => {
  const now = new Date();
  const estTime = new Date(now.toLocaleString('en-US', {
    timeZone: 'America/New_York'
  }));
  const currentDay = estTime.getDay();
  const currentHour = estTime.getHours();
  let daysUntilThursday = 0;
  if (currentDay === 4) {
    daysUntilThursday = currentHour >= 19 ? 7 : 0;
  } else if (currentDay < 4) {
    daysUntilThursday = 4 - currentDay;
  } else {
    daysUntilThursday = 7 - currentDay + 4;
  }
  const nextThursday = new Date(estTime);
  nextThursday.setDate(estTime.getDate() + daysUntilThursday);
  const day = nextThursday.getDate();
  const month = nextThursday.toLocaleString('es-ES', {
    month: 'long'
  }).toUpperCase();
  return `${month} ${day}`;
};
export function MetaJuevesPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [areaCode, setAreaCode] = useState('+1');
  const [phone, setPhone] = useState('');
  const [smsConsent, setSmsConsent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [fbclid, setFbclid] = useState('');
  const [ipAddress, setIpAddress] = useState('');
  const [fbp, setFbp] = useState('');
  const [fbc, setFbc] = useState('');
  const [utmParams, setUtmParams] = useState<Record<string, string>>({});
  const [userAgent, setUserAgent] = useState('');
  const [pageReferrer, setPageReferrer] = useState('');
  const [landingPageUrl, setLandingPageUrl] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const areaCodeRef = useRef<HTMLSelectElement>(null);
  const navigate = useNavigate();
  const thursdayDate = getNextThursday();
  useEffect(() => {
    window.scrollTo(0, 0);
    setLandingPageUrl(window.location.href);
    setPageReferrer(document.referrer || '');
    setUserAgent(navigator.userAgent || '');
    const urlParams = new URLSearchParams(window.location.search);
    const fbclidParam = urlParams.get('fbclid');
    if (fbclidParam) {
      setFbclid(fbclidParam);
      const fbcCookie = document.cookie.split('; ').find(row => row.startsWith('_fbc='))?.split('=')[1];
      setFbc(fbcCookie || `fb.1.${Date.now()}.${fbclidParam}`);
    } else {
      const fbcCookie = document.cookie.split('; ').find(row => row.startsWith('_fbc='))?.split('=')[1];
      if (fbcCookie) setFbc(fbcCookie);
    }
    const utms: Record<string, string> = {};
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term', 'utm_id'];
    utmKeys.forEach(key => {
      const val = urlParams.get(key);
      if (val) utms[key] = val;
    });
    const adParams = ['gclid', 'gbraid', 'wbraid', 'ttclid', 'ScCid', 'msclkid'];
    adParams.forEach(key => {
      const val = urlParams.get(key);
      if (val) utms[key] = val;
    });
    setUtmParams(utms);
    const readFbpCookie = () => {
      const fbpCookie = document.cookie.split('; ').find(row => row.startsWith('_fbp='))?.split('=')[1];
      if (fbpCookie) setFbp(fbpCookie);
    };
    readFbpCookie();
    const fbpTimer = setTimeout(readFbpCookie, 2000);
    fetch('https://api.ipify.org?format=json').then(res => res.json()).then(data => {
      if (data.ip) setIpAddress(data.ip);
    }).catch(() => {});
    (function (f: any, b: any, e: any, v: any, n: any, t: any, s: any) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      if (s && s.parentNode) {
        s.parentNode.insertBefore(t, s);
      } else {
        b.head.appendChild(t);
      }
    })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js', null, null, null);
    if (window.fbq) {
      window.fbq('init', '754551913878347');
      window.fbq('track', 'PageView');
    }
    // NewsBreak Pixel
    ;
    (function (e: any, n: any, t: any, i: any, p: any, a: any, s: any) {
      e[i] || ((p = e[i] = function () {
        p.process ? p.process.apply(p, arguments) : p.queue.push(arguments);
      }).queue = [], p.t = +new Date(), (a = n.createElement(t)).async = 1, a.src = 'https://static.newsbreak.com/business/tracking/nbpixel.js?t=' + 864e5 * Math.ceil(new Date().getTime() / 864e5), (s = n.getElementsByTagName(t)[0]).parentNode.insertBefore(a, s));
    })(window, document, 'script', 'nbpix', null, null, null);
    (window as any).nbpix && (window as any).nbpix('init', 'ID-2016254252686041090');
    (window as any).nbpix && (window as any).nbpix('event', 'pageload');
    return () => {
      clearTimeout(fbpTimer);
    };
  }, []);
  const handleOpenModal = () => {
    if (window.fbq) {
      window.fbq('trackCustom', 'OpenRegisterModal-MetaJueves');
    }
    setShowRegisterModal(true);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    const finalName = nameInputRef.current?.value?.trim() || name.trim() || '';
    const finalEmail = emailInputRef.current?.value?.trim() || email.trim() || '';
    const finalPhone = phoneInputRef.current?.value?.replace(/\D/g, '').trim() || phone.trim() || '';
    const finalAreaCode = areaCodeRef.current?.value || areaCode || '+1';
    if (!finalName || finalName.length < 2) {
      alert('Por favor ingresa tu nombre completo');
      return;
    }
    if (!finalEmail || !finalEmail.includes('@')) {
      alert('Por favor ingresa un email válido');
      return;
    }
    if (!finalPhone || finalPhone.length < 7) {
      alert('Por favor ingresa tu número de teléfono');
      return;
    }
    if (!smsConsent) {
      alert('Por favor marca la casilla de consentimiento de SMS para continuar.');
      return;
    }
    setIsSubmitting(true);
    try {
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Meta Jueves Webinar Registration'
        });
      }
      const payload = {
        name: finalName,
        email: finalEmail,
        phone: `${finalAreaCode}${finalPhone}`,
        first_name: finalName.split(' ')[0],
        last_name: finalName.split(' ').slice(1).join(' ') || finalName.split(' ')[0],
        full_name: finalName,
        fullPhone: `${finalAreaCode}${finalPhone}`,
        areaCode: finalAreaCode,
        source: '/meta-jueves',
        page_path: '/meta-jueves',
        sms_consent: smsConsent,
        fbclid,
        fbc,
        fbp,
        ip_address: ipAddress,
        user_agent: userAgent,
        ...utmParams,
        event_time: Math.floor(Date.now() / 1000),
        event_source_url: landingPageUrl || window.location.href,
        action_source: 'website',
        page_referrer: pageReferrer
      };
      await fetch(WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      navigate('/thank-you');
    } catch {
      navigate('/thank-you');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="min-h-screen bg-[#0a1428] text-white font-sans selection:bg-amber-400 selection:text-[#0a1428] overflow-x-hidden" data-id="element-3497" data-mp="1ksisredpys3x">
      <noscript data-id="element-3498" data-mp="2ckzo368tkp8p">
        <img height="1" width="1" style={{
        display: 'none'
      }} src="https://www.facebook.com/tr?id=754551913878347&ev=PageView&noscript=1" alt="" data-id="element-3499" data-mp="2ckgfig527d7c" />
      </noscript>

      {/* Ambient glows */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[600px] bg-amber-500/[0.08] blur-[140px] rounded-full pointer-events-none" data-id="element-3500" data-mp="flr0hhhu8ax5" />
      <div className="absolute top-[40%] right-0 w-[500px] h-[500px] bg-[#00f7fc]/[0.04] blur-[120px] rounded-full pointer-events-none" data-id="element-3501" data-mp="5x937a8roly9" />
      <div className="absolute inset-0 opacity-[0.015] pointer-events-none" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.4) 1px, transparent 1px)',
      backgroundSize: '60px 60px'
    }} data-id="element-3502" data-mp="3o1u6litaxrjd" />

      <main className="relative z-10 max-w-6xl mx-auto px-4 lg:px-8 pt-6 md:pt-14 pb-10" data-id="element-3503" data-mp="3j3993r5fplym">
        {/* ============ HERO ============ */}
        <section className="text-center mb-14 md:mb-24" data-id="element-3504" data-mp="1cdunuoxfdsus">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 md:px-4 md:py-1.5 mb-5 md:mb-7" data-id="element-3505" data-mp="1hcxq61m0tkz2">
            <Map className="w-3 h-3 md:w-3.5 md:h-3.5 text-amber-400" data-id="element-3506" data-mp="31sdqg745hy9g" />
            <span className="text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.18em] leading-none" data-id="element-3507" data-mp="2wwopbzchg9b1">
              Entrenamiento Gratis · Para Trabajadores 9–5
            </span>
          </div>

          <h1 className="text-[2rem] md:text-5xl lg:text-[4.5rem] xl:text-[5rem] font-black leading-[1.02] tracking-tight mb-5 md:mb-7 max-w-5xl mx-auto" data-id="element-3508" data-mp="36s8hqbru8pv1">
            No eres flojo.{' '}
            <span className="relative inline-block" data-id="element-3509" data-mp="3gitb9ksnn473">
              <span className="relative z-10 text-amber-400" data-id="element-3510" data-mp="3uivt570ypxmc">
                Te falta un mapa
              </span>
              <span className="absolute bottom-1 left-0 w-full h-3 bg-amber-400/15 -z-0 rounded-sm" data-id="element-3511" data-mp="3pjrj6e67rvuy" />
            </span>{' '}
            para crear dinero online con AI.
          </h1>

          <p className="text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed max-w-3xl mx-auto mb-7 md:mb-10" data-id="element-3512" data-mp="1v6jiw6zgys1x">
            Descubre en este entrenamiento gratis cómo usar AI para crear tu
            primer{' '}
            <span className="text-white font-semibold" data-id="element-3513" data-mp="21l8n3gxgmlrb">producto digital</span>,
            tu <span className="text-white font-semibold" data-id="element-3514" data-mp="26ktnlvsa3265">página</span> y tus{' '}
            <span className="text-white font-semibold" data-id="element-3515" data-mp="2bkfvdppc7q7k">assets de venta</span> en{' '}
            <span className="text-amber-400 font-bold" data-id="element-3516" data-mp="2bi3k1ve34e3h">48 horas</span> — aunque
            trabajes 9–5, no tengas seguidores, no seas experto y no sepas qué
            vender.
          </p>

          <div className="max-w-xl mx-auto mb-4" data-id="element-3517" data-mp="1ito85hq92bwd">
            <button type="button" onClick={handleOpenModal} className="group relative w-full bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-base md:text-lg lg:text-xl uppercase tracking-tight rounded-2xl py-5 md:py-6 px-6 shadow-[0_20px_60px_-15px_rgba(245,158,11,0.6)] hover:shadow-[0_20px_60px_-10px_rgba(245,158,11,0.75)] transition-all duration-300 transform hover:scale-[1.02] overflow-hidden" data-id="element-3518" data-mp="1nulpjs3fr1wt">
              <span className="relative z-10 flex flex-col items-center gap-1" data-id="element-3519" data-mp="39dz1gfbio8hh">
                <span className="flex items-center justify-center gap-2.5 lg:gap-3" data-id="element-3520" data-mp="39c4hb382kkeg">
                  <Ticket className="w-5 h-5 md:w-6 md:h-6" data-id="element-3521" data-mp="34d1e7obngo94" />
                  RESERVA TU LUGAR GRATIS
                  <ArrowRight className="w-5 h-5 md:w-6 md:h-6 transition-transform group-hover:translate-x-1" data-id="element-3522" data-mp="2zhvsapi8z9l1" />
                </span>
                <span className="text-[10px] md:text-xs font-bold opacity-80 normal-case tracking-normal" data-id="element-3523" data-mp="2pq82et1ca0lo">
                  Entrenamiento gratis · Cupos limitados · {thursdayDate} · 7PM
                  EST
                </span>
              </span>
              <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] group-hover:animate-shine pointer-events-none" data-id="element-3524" data-mp="2gr4mtz1ds3bx" />
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-2 max-w-xl mx-auto" data-id="element-3525" data-mp="1x3hyj1rm5xv2">
            {['Gratis', 'En vivo', 'Diseñado para principiantes'].map(t => <div key={t} className="flex items-center gap-1.5 bg-white/[0.03] border border-white/[0.06] rounded-full px-3 py-1.5" data-id="element-3526" data-mp="2245vs56fraxq">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" data-id="element-3527" data-mp="1s4f1o4lsexy6" />
                <span className="text-[11px] md:text-xs text-gray-400 font-medium" data-id="element-3528" data-mp="1s68a9pfyns8t">
                  {t}
                </span>
              </div>)}
          </div>
        </section>

        {/* ============ EMOTIONAL MIRROR ============ */}
        <section className="mb-14 md:mb-24" data-id="element-3529" data-mp="272h7klgajvil">
          <div className="max-w-4xl mx-auto" data-id="element-3530" data-mp="271fl7kttrmlk">
            <div className="text-center mb-8 md:mb-12" data-id="element-3531" data-mp="2b2r8vkt51xr4">
              <div className="inline-block text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-3532" data-mp="2b16epwaqkgts">
                Sé Por Qué Estás Aquí
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight" data-id="element-3533" data-mp="33kv8xan6u89y">
                Si esto te está pasando,{' '}
                <span className="text-amber-400" data-id="element-3534" data-mp="n5i48twqexl5">
                  este entrenamiento es para ti
                </span>
              </h2>
            </div>

            <div className="space-y-4 text-gray-300 text-sm md:text-lg leading-relaxed max-w-3xl mx-auto mb-10" data-id="element-3535" data-mp="11tv4i32ur2lg">
              <p data-id="element-3536" data-mp="wxefz7rqqm7u">
                Llegas cansado del trabajo, abres el teléfono y ves a todo el
                mundo hablando de AI, dinero online, productos digitales,
                automatización y oportunidades nuevas. Guardas videos. Tomas
                screenshots. Dices{' '}
                <span className="text-white font-semibold" data-id="element-3537" data-mp="j5hnv3q2ocbg">
                  "después lo veo."
                </span>{' '}
                Pero pasan los días y sigues igual: sin producto, sin página,
                sin oferta y sin un plan claro.
              </p>
              <p data-id="element-3538" data-mp="1pks3kf9ju5fa">
                Y lo peor no es que no quieras empezar. Lo peor es que sí
                quieres, pero{' '}
                <span className="text-white font-semibold" data-id="element-3539" data-mp="3j4alss5dx0c4">
                  cada vez que intentas hacerlo, aparecen demasiadas preguntas
                  al mismo tiempo.
                </span>
              </p>
            </div>

            {/* Mirror table */}
            <div className="bg-white/[0.025] border border-white/[0.08] rounded-2xl overflow-hidden mb-8" data-id="element-3540" data-mp="1mhl5c4qs4nw">
              <div className="hidden md:grid grid-cols-2 gap-px bg-white/[0.06] text-[11px] font-black uppercase tracking-[0.18em]" data-id="element-3541" data-mp="6k9d265dzqs3">
                <div className="bg-[#0a1428] px-6 py-3 text-gray-400" data-id="element-3542" data-mp="al32x52e3fnm">
                  Lo que piensas
                </div>
                <div className="bg-[#0a1428] px-6 py-3 text-amber-400" data-id="element-3543" data-mp="kl28qu7fcrws">
                  Lo que realmente está pasando
                </div>
              </div>
              <div className="divide-y divide-white/[0.06]" data-id="element-3544" data-mp="2hex3jpjn2ej9">
                {MIRROR_ROWS.map((row, i) => <div key={i} className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/[0.04]" data-id="element-3545" data-mp="2md8cly240l0f" data-mpi={i}>
                    <div className="bg-[#0a1428] px-5 md:px-6 py-4 md:py-5 flex items-start gap-3" data-id="element-3546" data-mp="2etpjgyz5jbe4">
                      <CircleX className="w-4 h-4 text-red-400/80 flex-shrink-0 mt-1" data-id="element-3547" data-mp="2a0mqaryae3yg" />
                      <span className="text-sm md:text-base text-gray-300 italic" data-id="element-3548" data-mp="2545zfpzpdbho">
                        {row.think}
                      </span>
                    </div>
                    <div className="bg-[#0a1428] px-5 md:px-6 py-4 md:py-5 flex items-start gap-3" data-id="element-3549" data-mp="2xsbouq1pbonu">
                      <CircleDot className="w-4 h-4 text-amber-400 flex-shrink-0 mt-1" data-id="element-3550" data-mp="2ss5u3104xm1j" />
                      <span className="text-sm md:text-base text-white font-medium" data-id="element-3551" data-mp="2ntcf14579mz9">
                        {row.real}
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>

            {/* Reframe */}
            <div className="bg-amber-500/[0.06] border border-amber-500/30 rounded-2xl p-6 md:p-8 lg:p-10" data-id="element-3552" data-mp="1cfqmra41n59i">
              <div className="flex items-start gap-3 md:gap-4" data-id="element-3553" data-mp="17hgqsxahw0mu">
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center flex-shrink-0" data-id="element-3554" data-mp="78naqmyswfrc">
                  <Lightbulb className="w-5 h-5 md:w-6 md:h-6 text-amber-400" data-id="element-3555" data-mp="3tn6ochfybqwr" />
                </div>
                <div className="flex-1 space-y-3 text-sm md:text-base lg:text-lg leading-relaxed" data-id="element-3556" data-mp="3oomza8629z06">
                  <p className="text-white font-semibold" data-id="element-3557" data-mp="q79e68hsiiyf">
                    El problema no eres tú.
                  </p>
                  <p className="text-gray-300" data-id="element-3558" data-mp="l8odk7ymlx9g">
                    El problema es que has estado intentando crear un negocio
                    online como si tuvieras que descubrirlo todo solo.
                  </p>
                  <p className="text-gray-300" data-id="element-3559" data-mp="c6f0pbfvxrsw">
                    <span className="text-amber-400 font-bold" data-id="element-3560" data-mp="30yj1wijguvhm">
                      48 Hrs AI Cash Machine
                    </span>{' '}
                    fue creado para darte una ruta simple: qué crear, cómo
                    crearlo con AI, cómo convertirlo en una oferta y cómo
                    preparar los assets para venderlo.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ WEBINAR PROMISE ============ */}
        <section className="mb-14 md:mb-24" data-id="element-3561" data-mp="1q6td216m3nt1">
          <div className="text-center mb-8 md:mb-14" data-id="element-3562" data-mp="1q7urmfmu6sfi">
            <div className="inline-block text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-3563" data-mp="1v6foc52vqtz7">
              La Promesa
            </div>
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight max-w-4xl mx-auto" data-id="element-3564" data-mp="246b5qrn13l7o">
              En este entrenamiento gratis{' '}
              <span className="text-amber-400" data-id="element-3565" data-mp="2944dvq7w6pal">vas a descubrir:</span>
            </h2>
            <p className="mt-5 text-sm md:text-base lg:text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-3566" data-mp="9tcx0cgz8wmg">
              Cómo convertir una idea simple en un producto digital usando AI —
              sin pasar meses estudiando, sin contratar a nadie y sin sentir que
              necesitas ser un experto antes de empezar.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 max-w-5xl mx-auto mb-10" data-id="element-3567" data-mp="1r2yr3s8ybqah">
            {PROMISES.map(p => <div key={p.title} className="group relative bg-white/[0.025] border border-white/[0.08] rounded-2xl p-5 md:p-6 lg:p-7 hover:border-amber-500/40 hover:bg-white/[0.04] transition-all duration-300" data-id="element-3568" data-mp="1m3drrgpvdfx8">
                <div className="flex items-start gap-3 md:gap-4" data-id="element-3569" data-mp="1w10cwkxri3pn">
                  <div className="w-11 h-11 md:w-12 md:h-12 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0" data-id="element-3570" data-mp="29tggpnyhm5rm">
                    <p.icon className="w-5 h-5 text-amber-400" data-id="element-3571" data-mp="25so8my1cq6wx" />
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-3572" data-mp="2js5qetg8cjws">
                    <h3 className="text-base md:text-lg font-black text-white mb-2 leading-snug" data-id="element-3573" data-mp="2est6ohknnsyf">
                      {p.title}
                    </h3>
                    <p className="text-sm md:text-[15px] text-gray-400 leading-relaxed" data-id="element-3574" data-mp="2yf4fwivj148v">
                      <span className="text-amber-400/80 text-[10px] md:text-xs font-black uppercase tracking-[0.16em] block mb-1" data-id="element-3575" data-mp="33cxlpasx3w8m">
                        Por qué importa
                      </span>
                      {p.why}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="max-w-md mx-auto" data-id="element-3576" data-mp="2fm0iqnj3o99i">
            <button type="button" onClick={handleOpenModal} className="group w-full flex items-center justify-center gap-2.5 bg-white/[0.04] border border-amber-500/30 hover:bg-amber-500/10 hover:border-amber-400 text-white font-black text-sm md:text-base uppercase tracking-tight rounded-xl py-4 px-6 transition-all duration-300" data-id="element-3577" data-mp="2kfmxqmcqkp8h">
              <Ticket className="w-4 h-4 md:w-5 md:h-5 text-amber-400" data-id="element-3578" data-mp="1wpski3oi8ynp" />
              Sí, Quiero Entrar Al Entrenamiento Gratis
              <ArrowRight className="w-4 h-4 md:w-5 md:h-5 text-amber-400 transition-transform group-hover:translate-x-1" data-id="element-3579" data-mp="1tnebq9a4qpc5" />
            </button>
          </div>
        </section>

        {/* ============ MECHANISM ============ */}
        <section className="mb-14 md:mb-24" data-id="element-3580" data-mp="1jwta0cz5t4p0">
          <div className="max-w-5xl mx-auto" data-id="element-3581" data-mp="1jvi1jzurr8rk">
            <div className="text-center mb-8 md:mb-12" data-id="element-3582" data-mp="1ez9u78mtcjuy">
              <div className="inline-block text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-3583" data-mp="1ewnmn8zxa4cq">
                El Mecanismo
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight max-w-4xl mx-auto" data-id="element-3584" data-mp="15w8q1dhid2tx">
                El método no es "ver más videos de AI".{' '}
                <span className="text-amber-400" data-id="element-3585" data-mp="w0fg81v6bnfu">
                  Es construir tu primer activo digital
                </span>{' '}
                con un mapa de 48 horas.
              </h2>
            </div>

            <div className="space-y-4 text-gray-300 text-sm md:text-base lg:text-lg leading-relaxed max-w-3xl mx-auto mb-10" data-id="element-3586" data-mp="2qeq0ry4qjvft">
              <p data-id="element-3587" data-mp="2yufcugyxcx1e">
                La mayoría de personas no está fallando porque le falte
                motivación. Está fallando porque{' '}
                <span className="text-white font-semibold" data-id="element-3588" data-mp="37mh9s59sbv6x">
                  consume demasiada información sin una secuencia de ejecución.
                </span>
              </p>
              <p data-id="element-3589" data-mp="2fvtakyro8zvw">
                Un video te dice que hagas prompts. Otro te dice que vendas
                servicios. Otro te dice que crees contenido. Otro te dice que
                hagas ads. Al final tienes muchas piezas, pero no sabes en qué
                orden ponerlas.
              </p>
              <p className="text-white font-semibold" data-id="element-3590" data-mp="2tvkv7icp1eiv">
                48 Hrs AI Cash Machine organiza el proceso en una ruta clara:
              </p>
            </div>

            {/* Phases */}
            <div className="relative" data-id="element-3591" data-mp="95dbxlf6c876">
              {/* Vertical connector */}
              <div className="hidden md:block absolute left-[27px] top-6 bottom-6 w-px bg-gradient-to-b from-amber-500/40 via-amber-500/20 to-transparent pointer-events-none" data-id="element-3592" data-mp="97g9qrmkj5mg" />
              <div className="space-y-3 md:space-y-4" data-id="element-3593" data-mp="3amzjfefinobz">
                {PHASES.map((p, idx) => <div key={p.phase} className="relative bg-white/[0.025] border border-white/[0.08] rounded-2xl p-5 md:p-6 hover:border-amber-500/30 transition-colors" data-id="element-3594" data-mp="3pjqni104xo7x" data-mpi={idx}>
                    <div className="flex items-start gap-4 md:gap-5" data-id="element-3595" data-mp="2wvupmsytxq9k">
                      <div className="relative w-12 h-12 md:w-14 md:h-14 rounded-2xl bg-amber-500/10 border border-amber-500/30 flex items-center justify-center flex-shrink-0" data-id="element-3596" data-mp="2wu02d8035m2b">
                        <p.icon className="w-5 h-5 md:w-6 md:h-6 text-amber-400" data-id="element-3597" data-mp="36ngy4hjs9b8m" />
                        <span className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-amber-400 text-[#0a1428] text-[10px] font-black flex items-center justify-center border-2 border-[#0a1428]" data-id="element-3598" data-mp="31ufgi1s6yxlc">
                          {idx + 1}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0 pt-1.5 md:pt-2" data-id="element-3599" data-mp="ong56znmcck1">
                        <div className="text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.18em] mb-1" data-id="element-3600" data-mp="texxz0fhn6tx">
                          {p.phase}
                        </div>
                        <p className="text-base md:text-lg font-bold text-white leading-snug" data-id="element-3601" data-mp="jpeeptnv5ayj">
                          {p.title}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>

            <p className="mt-10 text-center text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-relaxed" data-id="element-3602" data-mp="1cbzeniiemvdp">
              No se trata de prometer dinero mágico. Se trata de enseñarte a{' '}
              <span className="text-white font-semibold" data-id="element-3603" data-mp="12e2wi6r1v063">
                crear un activo real
              </span>{' '}
              que puedas entender, construir y lanzar con dirección.
            </p>
          </div>
        </section>

        {/* ============ IDENTITY SHIFT ============ */}
        <section className="mb-14 md:mb-24" data-id="element-3604" data-mp="ipll1qhzcaf1">
          <div className="max-w-4xl mx-auto" data-id="element-3605" data-mp="doph5cxmoo9m">
            <div className="text-center mb-8 md:mb-10" data-id="element-3606" data-mp="dnw5pynq5al5">
              <div className="inline-block text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-3607" data-mp="9n3ytc2brhs1">
                Para Quién Es
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight" data-id="element-3608" data-mp="11es3rdirx20u">
                Esto es para el trabajador 9–5 que sabe que AI{' '}
                <span className="text-amber-400" data-id="element-3609" data-mp="xdq2kr5aqfw8">no es una moda…</span> es una
                oportunidad.
              </h2>
            </div>

            <div className="bg-gradient-to-br from-amber-950/20 to-[#0a1428] border border-amber-500/20 rounded-3xl p-6 md:p-10 lg:p-12 space-y-5 text-sm md:text-base lg:text-lg leading-relaxed text-gray-300" data-id="element-3610" data-mp="nnequifdf2es">
              <p data-id="element-3611" data-mp="1gbivd0m7h9fw">
                Tal vez hoy te ves como alguien que está tarde. Alguien que no
                sabe suficiente. Alguien que tiene que esperar a tener más
                tiempo, más dinero, más experiencia o más confianza.
              </p>
              <p className="text-white font-semibold" data-id="element-3612" data-mp="1soe81vezjzo3">
                Pero la verdad es que no necesitas convertirte en un "gurú" para
                empezar. Necesitas convertirte en una persona que puede seguir
                un sistema y construir su primer asset digital paso a paso.
              </p>
              <ul className="space-y-2.5 pt-2" data-id="element-3613" data-mp="26k8jal38f14d">
                {['No necesitas ser el más técnico.', 'No necesitas tener miles de seguidores.', 'No necesitas tener todo perfecto.', 'Necesitas empezar con una ruta clara.'].map(line => <li key={line} className="flex items-start gap-3" data-id="element-3614" data-mp="2lapjy281ln07">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-amber-400 flex-shrink-0 mt-1" data-id="element-3615" data-mp="2ld1y04aojd7z" />
                    <span data-id="element-3616" data-mp="2pahlvw2xkuwl">{line}</span>
                  </li>)}
              </ul>
              <p className="pt-2 text-white font-semibold" data-id="element-3617" data-mp="2uc6tt2uuwsgg">
                Este entrenamiento es para ti si estás cansado de mirar cómo
                otros usan AI mientras tú sigues guardando videos sin ejecutar.
              </p>
            </div>
          </div>
        </section>

        {/* ============ OBJECTIONS ============ */}
        <section className="mb-14 md:mb-24" data-id="element-3618" data-mp="3s1os9jsu7hdp">
          <div className="max-w-4xl mx-auto" data-id="element-3619" data-mp="3s2hnpomcikgj">
            <div className="text-center mb-8 md:mb-12" data-id="element-3620" data-mp="jlwnaw7s4zp1">
              <div className="inline-block text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-3621" data-mp="jn7t11kschis">
                Resuelve Tus Dudas
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight" data-id="element-3622" data-mp="9v209pmplw2j">
                "Pero yo no sé si{' '}
                <span className="text-amber-400" data-id="element-3623" data-mp="etbw82g9d0p7">esto es para mí</span>…"
              </h2>
              <p className="mt-5 text-sm md:text-base text-gray-400 max-w-2xl mx-auto leading-relaxed" data-id="element-3624" data-mp="12j68oiwo0n8x">
                Si tienes esa duda, probablemente este entrenamiento te puede
                ayudar — fue creado precisamente para personas que están
                empezando y quieren claridad antes de tomar acción.
              </p>
            </div>

            <div className="space-y-3 mb-10" data-id="element-3625" data-mp="30upw5xr948ma">
              {OBJECTIONS.map((obj, i) => <div key={i} className="grid grid-cols-1 md:grid-cols-[1fr_1.5fr] gap-3 md:gap-5 bg-white/[0.025] border border-white/[0.08] rounded-2xl p-5 md:p-6 hover:border-amber-500/30 transition-colors" data-id="element-3626" data-mp="30w12o5c2zuh3" data-mpi={i}>
                  <div className="flex items-start gap-3" data-id="element-3627" data-mp="24474wkdmmhom">
                    <Quote className="w-4 h-4 md:w-5 md:h-5 text-gray-500 flex-shrink-0 mt-1" data-id="element-3628" data-mp="245s10efla8ol" />
                    <p className="text-sm md:text-base text-gray-300 italic font-medium leading-snug" data-id="element-3629" data-mp="2i59qkw0plpz0">
                      {obj.o}
                    </p>
                  </div>
                  <div className="flex items-start gap-3 md:border-l md:border-amber-500/20 md:pl-5" data-id="element-3630" data-mp="3thkdwmwootw2">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-amber-400 flex-shrink-0 mt-1" data-id="element-3631" data-mp="3oize2olcgc85" />
                    <p className="text-sm md:text-base text-white leading-snug" data-id="element-3632" data-mp="2m5l2m4p4qbxa">
                      {obj.r}
                    </p>
                  </div>
                </div>)}
            </div>

            <div className="text-center" data-id="element-3633" data-mp="2v8cdo3e3f5oe">
              <button type="button" onClick={handleOpenModal} className="group inline-flex items-center justify-center gap-2.5 bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-sm md:text-base uppercase tracking-tight rounded-xl py-4 px-7 shadow-[0_15px_40px_-15px_rgba(245,158,11,0.5)] transition-all duration-300 transform hover:scale-[1.02]" data-id="element-3634" data-mp="3065j2u7knvnm">
                <Compass className="w-4 h-4 md:w-5 md:h-5" data-id="element-3635" data-mp="2h700fzqbhi67" />
                Quiero Ver El Mapa Gratis
                <ArrowRight className="w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:translate-x-1" data-id="element-3636" data-mp="1js5ozzver08a" />
              </button>
            </div>
          </div>
        </section>

        {/* ============ BEFORE / AFTER ============ */}
        <section className="mb-14 md:mb-24" data-id="element-3637" data-mp="1h6xusgh7igpf">
          <div className="max-w-5xl mx-auto" data-id="element-3638" data-mp="1c8d3x7nuh9dn">
            <div className="text-center mb-8 md:mb-12" data-id="element-3639" data-mp="1c9o7b6ddof48">
              <div className="inline-block text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.2em] mb-3" data-id="element-3640" data-mp="17907bv768nxv">
                Con Qué Te Vas
              </div>
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight max-w-4xl mx-auto" data-id="element-3641" data-mp="13a15wkknf9b6">
                Vas a entender qué hacer primero, qué hacer después y{' '}
                <span className="text-amber-400" data-id="element-3642" data-mp="tgkdnl05u29m">qué dejar de hacer.</span>
              </h2>
              <p className="mt-5 text-sm md:text-base text-gray-400 max-w-3xl mx-auto leading-relaxed" data-id="element-3643" data-mp="ojbv3jb3nw7u">
                Cuando no tienes un mapa, cualquier paso se siente pesado. Pero
                cuando sabes qué construir y en qué orden, la acción se vuelve
                más simple.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6" data-id="element-3644" data-mp="aldhig4ls3te">
              {/* Before */}
              <div className="bg-red-950/15 border border-red-500/20 rounded-2xl p-5 md:p-7" data-id="element-3645" data-mp="2emufxz3we6ny">
                <div className="flex items-center gap-2 mb-5" data-id="element-3646" data-mp="20vh0aw941sy7">
                  <CircleX className="w-4 h-4 text-red-400" data-id="element-3647" data-mp="20uxqy3xj0cvs" />
                  <span className="text-red-400 text-[10px] md:text-xs font-black uppercase tracking-[0.18em]" data-id="element-3648" data-mp="25u0q57qtrovu">
                    Antes del entrenamiento
                  </span>
                </div>
                <ul className="space-y-3" data-id="element-3649" data-mp="1vwminxmvqafo">
                  {BEFORE_AFTER.map((row, i) => <li key={i} className="flex items-start gap-3 text-sm md:text-base text-gray-300 leading-snug" data-id="element-3650" data-mp="1hwl97tsjtuol" data-mpi={i}>
                      <span className="text-red-400/70 mt-1 flex-shrink-0" data-id="element-3651" data-mp="1mv691s3w2cci">
                        —
                      </span>
                      <span data-id="element-3652" data-mp="3cbxiuyfyoppa">{row.before}</span>
                    </li>)}
                </ul>
              </div>

              {/* After */}
              <div className="bg-amber-500/[0.06] border border-amber-500/30 rounded-2xl p-5 md:p-7" data-id="element-3653" data-mp="3h7cv5lybmdjl">
                <div className="flex items-center gap-2 mb-5" data-id="element-3654" data-mp="3mejcma6fo8nf">
                  <CheckCircle2 className="w-4 h-4 text-amber-400" data-id="element-3655" data-mp="3mg47y20knvmc" />
                  <span className="text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.18em]" data-id="element-3656" data-mp="40tjt07fhw6o">
                    Después del entrenamiento
                  </span>
                </div>
                <ul className="space-y-3" data-id="element-3657" data-mp="dxojvnjco4n2">
                  {BEFORE_AFTER.map((row, i) => <li key={i} className="flex items-start gap-3 text-sm md:text-base text-white font-medium leading-snug" data-id="element-3658" data-mp="8zwpmmv8g6fp" data-mpi={i}>
                      <ArrowRight className="w-4 h-4 text-amber-400 mt-1 flex-shrink-0" data-id="element-3659" data-mp="myedckl51ixm" />
                      <span data-id="element-3660" data-mp="ixkp1q3sa247">{row.after}</span>
                    </li>)}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ============ URGENCY ============ */}
        <section className="mb-14 md:mb-24" data-id="element-3661" data-mp="2kq8u84zzmzmm">
          <div className="max-w-4xl mx-auto" data-id="element-3662" data-mp="2krjx82lm03co">
            <div className="relative bg-gradient-to-br from-amber-950/25 via-[#0a1428] to-[#0a1428] border border-amber-500/30 rounded-3xl p-6 md:p-10 lg:p-14 overflow-hidden" data-id="element-3663" data-mp="npmj7enhzzqf">
              <div className="absolute -top-20 -right-20 w-60 h-60 bg-amber-400/10 blur-3xl rounded-full pointer-events-none" data-id="element-3664" data-mp="xm7om13fjcus" />
              <div className="relative text-center" data-id="element-3665" data-mp="12mvgeozhfxq2">
                <div className="inline-flex items-center gap-2 bg-red-500/10 border border-red-500/30 rounded-full px-3.5 py-1.5 mb-5" data-id="element-3666" data-mp="16nf8he578b5c">
                  <Clock className="w-3.5 h-3.5 text-red-400" data-id="element-3667" data-mp="1blgt43i9wiix" />
                  <span className="text-red-400 text-[10px] md:text-xs font-black uppercase tracking-[0.18em] leading-none" data-id="element-3668" data-mp="1gd8jacxiom8j">
                    Urgencia
                  </span>
                </div>

                <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight mb-5 md:mb-6" data-id="element-3669" data-mp="1qbej8ozrjiar">
                  AI no va a esperar a que{' '}
                  <span className="text-amber-400" data-id="element-3670" data-mp="1udjc3lie0awc">te sientas listo.</span>
                </h2>

                <div className="space-y-4 text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto mb-8" data-id="element-3671" data-mp="3ewlsetd4o6qf">
                  <p data-id="element-3672" data-mp="3avthzwsirvsk">
                    Cada mes aparecen nuevas herramientas, nuevas oportunidades
                    y nuevas personas usando AI para crear productos, páginas,
                    ads y negocios más rápido que antes.
                  </p>
                  <p className="text-white font-semibold" data-id="element-3673" data-mp="2cc76gkx5tvq">
                    Si sigues esperando a sentirte listo, puedes terminar viendo
                    esta oportunidad como otra cosa que dejaste pasar.
                  </p>
                  <p data-id="element-3674" data-mp="3or5b5zr11e7d">
                    No necesitas tenerlo todo resuelto hoy. Solo necesitas dar
                    el próximo paso correcto.
                  </p>
                </div>

                <div className="max-w-md mx-auto" data-id="element-3675" data-mp="l5g6ahgwoyxq">
                  <button type="button" onClick={handleOpenModal} className="group relative w-full bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-base md:text-lg uppercase tracking-tight rounded-2xl py-5 px-6 shadow-[0_20px_60px_-15px_rgba(245,158,11,0.6)] transition-all duration-300 transform hover:scale-[1.02] overflow-hidden" data-id="element-3676" data-mp="gd5395qvcx0y">
                    <span className="relative z-10 flex items-center justify-center gap-2.5" data-id="element-3677" data-mp="3fvdamqy32iro">
                      <Ticket className="w-5 h-5" data-id="element-3678" data-mp="7g3b9qlkvrnl" />
                      Reserva Tu Lugar Gratis Ahora
                      <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-3679" data-mp="cfock7o80c3h" />
                    </span>
                    <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] group-hover:animate-shine pointer-events-none" data-id="element-3680" data-mp="3ukj4s0i2disp" />
                  </button>
                  <p className="mt-3 text-[11px] md:text-xs text-gray-400 leading-relaxed" data-id="element-3681" data-mp="2j88kyjlasx0e">
                    Regístrate gratis y aprende el mapa de 48 horas para crear
                    tu primer producto digital con AI.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ============ FINAL CLOSE ============ */}
        <section className="mb-10 md:mb-16" data-id="element-3682" data-mp="2pjrzac2u5kt0">
          <div className="max-w-4xl mx-auto text-center" data-id="element-3683" data-mp="2kmrt815zki1w">
            <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-3 py-1 md:px-4 md:py-1.5 mb-5 md:mb-6" data-id="element-3684" data-mp="2kl8dqcs5mb16">
              <Workflow className="w-3 h-3 md:w-3.5 md:h-3.5 text-amber-400" data-id="element-3685" data-mp="1rzoub7xee5b4" />
              <span className="text-amber-400 text-[10px] md:text-xs font-black uppercase tracking-[0.18em] leading-none" data-id="element-3686" data-mp="26njoksr20hqb">
                Cierre
              </span>
            </div>

            <h2 className="text-2xl md:text-4xl lg:text-5xl font-black leading-tight mb-6" data-id="element-3687" data-mp="1e6fpzlob49zg">
              No necesitas más motivación.{' '}
              <span className="text-amber-400" data-id="element-3688" data-mp="197cvuw9v7o69">Necesitas un sistema.</span>
            </h2>

            <div className="space-y-4 text-sm md:text-base lg:text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto mb-8 md:mb-10" data-id="element-3689" data-mp="afktrpgz85hm">
              <p data-id="element-3690" data-mp="5k5dkqvhxxi1">
                Si has estado sintiendo que quieres hacer algo con AI, pero no
                sabes por dónde empezar, este entrenamiento te va a dar el mapa.
              </p>
              <ul className="space-y-2 text-left max-w-md mx-auto pt-2" data-id="element-3691" data-mp="ego5q0vwlhdk">
                {['No para que sigas consumiendo contenido.', 'No para que guardes otro video.', 'No para que sigas pensando "algún día".'].map(line => <li key={line} className="flex items-start gap-3" data-id="element-3692" data-mp="ipsiytbjqzn">
                    <CircleX className="w-4 h-4 text-red-400/80 flex-shrink-0 mt-1" data-id="element-3693" data-mp="k0waymrkyqr" />
                    <span data-id="element-3694" data-mp="3rvu8ozd71xwo">{line}</span>
                  </li>)}
              </ul>
              <p className="text-white font-semibold pt-2" data-id="element-3695" data-mp="346rhazsrcqoz">
                Para que puedas ver una ruta clara y decir: "Ahora sí sé qué
                tengo que construir."
              </p>
            </div>

            <div className="max-w-md mx-auto" data-id="element-3696" data-mp="1epvyb8vxaizd">
              <button type="button" onClick={handleOpenModal} className="group relative w-full bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-base md:text-lg uppercase tracking-tight rounded-2xl py-5 px-6 shadow-[0_20px_60px_-15px_rgba(245,158,11,0.6)] transition-all duration-300 transform hover:scale-[1.02] overflow-hidden" data-id="element-3697" data-mp="1jhdyhuq4gfja">
                <span className="relative z-10 flex items-center justify-center gap-2.5" data-id="element-3698" data-mp="22g9sb4y2irwl">
                  <Ticket className="w-5 h-5" data-id="element-3699" data-mp="22dxdv1riqzoa" />
                  Entrar Al Entrenamiento Gratis
                  <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" data-id="element-3700" data-mp="1oiuylo0s7xyt" />
                </span>
                <div className="absolute top-0 left-[-100%] w-full h-full bg-gradient-to-r from-transparent via-white/40 to-transparent skew-x-[-25deg] group-hover:animate-shine pointer-events-none" data-id="element-3701" data-mp="d1lh3717rg05" />
              </button>
              <p className="mt-3 text-[11px] md:text-xs text-gray-400 leading-relaxed" data-id="element-3702" data-mp="3u3y21gem7w0c">
                Aprende cómo crear tu primer producto digital con AI en 48
                horas, incluso si trabajas 9–5, no tienes seguidores y no sabes
                qué vender todavía.
              </p>
            </div>

            <p className="mt-10 text-xs md:text-sm text-gray-500 italic max-w-2xl mx-auto leading-relaxed" data-id="element-3703" data-mp="3gbi58xcb4u7v">
              Deja de guardar videos de AI. Aprende el mapa para crear tu primer
              activo digital.
            </p>
          </div>
        </section>
      </main>

      <Footer data-id="element-3704" data-mp="32bqlsh30ulmh" />

      {/* Registration Modal */}
      {showRegisterModal && <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm overflow-y-auto" onClick={() => setShowRegisterModal(false)} data-id="element-3705" data-mp="2xdfd49ogqh5u">
          <div className="bg-[#0f1b35] border border-amber-500/20 rounded-2xl max-w-md w-full p-6 md:p-8 relative shadow-[0_0_50px_rgba(245,158,11,0.15)] my-8" onClick={e => e.stopPropagation()} data-id="element-3706" data-mp="10kdr0kurpu45">
            <button onClick={() => setShowRegisterModal(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors p-1" aria-label="Cerrar" data-id="element-3707" data-mp="135bj5iangg0y">
              <X className="w-6 h-6" data-id="element-3708" data-mp="1cwpo45lde81h" />
            </button>

            <div className="text-center mb-6" data-id="element-3709" data-mp="1hu8064uop3dp">
              <div className="inline-flex items-center gap-2 bg-amber-500/15 border border-amber-500/40 rounded-full px-3 py-1 mb-3" data-id="element-3710" data-mp="1hty88bq2wm5d">
                <Ticket className="w-3.5 h-3.5 text-amber-400" data-id="element-3711" data-mp="k923wro0hjfd" />
                <span className="text-amber-400 text-[10px] font-black uppercase tracking-[0.18em] leading-none" data-id="element-3712" data-mp="p6c3h0547bgx">
                  Cupo Gratis
                </span>
              </div>
              <h2 className="text-2xl md:text-3xl font-black text-white mb-2 uppercase tracking-tight" data-id="element-3713" data-mp="y79zav5egcof">
                Completa Tu Registro
              </h2>
              <p className="text-gray-400 text-sm" data-id="element-3714" data-mp="29ldqjtklrmps">
                Ingresa tus datos para reservar tu lugar en el entrenamiento.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-3715" data-mp="284qgbzlktcqn">
              {/* Name */}
              <div className="space-y-1" data-id="element-3716" data-mp="23787sabh0zj6">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.14em] ml-2" data-id="element-3717" data-mp="2i2ehb79rzgh9">
                  Nombre Completo
                </label>
                <div className="relative" data-id="element-3718" data-mp="2d5nywdai08so">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-3719" data-mp="2r2uczuka1jbk">
                    <User className="h-5 w-5 text-gray-500" data-id="element-3720" data-mp="2m519j9acz3ez" />
                  </div>
                  <input ref={nameInputRef} type="text" name="name" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" required minLength={2} autoComplete="name" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-3721" data-mp="30tp2r8wqf11r" />
                </div>
              </div>

              {/* Email */}
              <div className="space-y-1" data-id="element-3722" data-mp="3oh2eq10qb6ix">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.14em] ml-2" data-id="element-3723" data-mp="3tfdpcdyull27">
                  Tu Mejor Email
                </label>
                <div className="relative" data-id="element-3724" data-mp="3ffl3avy9nt0k">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-3725" data-mp="3kefv2ne7orwt">
                    <Mail className="h-5 w-5 text-gray-500" data-id="element-3726" data-mp="35pjcvxifxyih" />
                  </div>
                  <input ref={emailInputRef} type="email" name="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="ejemplo@correo.com" required autoComplete="email" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-3727" data-mp="3aghwp72n1eqh" />
                </div>
              </div>

              {/* Phone */}
              <div className="space-y-1" data-id="element-3728" data-mp="2rpqq2da1c2fs">
                <label className="text-[11px] font-black text-gray-400 uppercase tracking-[0.14em] ml-2" data-id="element-3729" data-mp="xa1ssrq39zoy">
                  WhatsApp / Teléfono
                </label>
                <div className="flex gap-2" data-id="element-3730" data-mp="si1z3h0m430t">
                  <div className="relative w-1/3" data-id="element-3731" data-mp="oh898i3m0e5a">
                    <div className="absolute inset-y-0 left-0 pl-2 flex items-center pointer-events-none" data-id="element-3732" data-mp="ohzsctg0sjeo">
                      <Globe className="h-4 w-4 text-gray-500" data-id="element-3733" data-mp="jgk996xekmyx" />
                    </div>
                    <select ref={areaCodeRef} name="areaCode" value={areaCode} onChange={e => setAreaCode(e.target.value)} className="w-full pl-8 pr-2 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all appearance-none text-sm" data-id="element-3734" data-mp="1g84lbghuieri">
                      {COUNTRY_CODES.map(c => <option key={c.code} value={c.code} className="bg-gray-900" data-id="element-3735" data-mp="3ry6yr7ptkur5">
                          {c.code} {c.country}
                        </option>)}
                    </select>
                  </div>
                  <div className="relative w-2/3" data-id="element-3736" data-mp="3cw878g6xukbn">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-3737" data-mp="3ctca2u2wzzo9">
                      <Phone className="h-5 w-5 text-gray-500" data-id="element-3738" data-mp="3hs712jb1cujg" />
                    </div>
                    <input ref={phoneInputRef} type="tel" name="phone" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ''))} placeholder="Número" required minLength={7} autoComplete="tel" className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 focus:bg-white/10 transition-all" data-id="element-3739" data-mp="3mrjn51u31xkz" />
                  </div>
                </div>
              </div>

              {/* SMS consent */}
              <label className="flex items-start gap-3 cursor-pointer group pt-1" data-id="element-3740" data-mp="ed1jrdf3767p">
                <div className="relative flex-shrink-0 mt-0.5" data-id="element-3741" data-mp="2b7o00qr6pa6q">
                  <input type="checkbox" checked={smsConsent} onChange={e => setSmsConsent(e.target.checked)} className="sr-only peer" data-id="element-3742" data-mp="2g4edd6bltrog" />
                  <div className="w-5 h-5 border-2 border-gray-500 rounded bg-white/5 peer-checked:bg-amber-400 peer-checked:border-amber-400 transition-all flex items-center justify-center" data-id="element-3743" data-mp="lvra1mm7iah5">
                    <CheckCircle2 className="w-3 h-3 text-[#0a1428] opacity-0 peer-checked:opacity-100" data-id="element-3744" data-mp="vmm0z4aedy8x" />
                  </div>
                </div>
                <span className="text-[11px] md:text-xs text-gray-400 leading-relaxed" data-id="element-3745" data-mp="2y80mhvu6pu7">
                  Acepto recibir mensajes SMS de True Marketer con detalles del
                  entrenamiento y recordatorios. Mensajes y tarifas de datos
                  pueden aplicar. Puedes darte de baja respondiendo STOP.
                </span>
              </label>

              <button type="submit" disabled={isSubmitting} className="group relative w-full bg-gradient-to-b from-amber-400 to-amber-500 hover:from-amber-300 hover:to-amber-400 text-[#0a1428] font-black text-sm md:text-base uppercase tracking-tight rounded-xl py-4 px-6 shadow-[0_15px_40px_-15px_rgba(245,158,11,0.5)] transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-3746" data-mp="7w9mfaknd39d">
                {isSubmitting ? 'Registrando…' : 'Reservar Mi Lugar Gratis'}
              </button>

              <p className="text-center text-[10px] text-gray-500 leading-relaxed" data-id="element-3747" data-mp="1gzvyyyg563km">
                <ShieldCheck className="w-3 h-3 inline text-emerald-400 mr-1" data-id="element-3748" data-mp="1lxo26om8lc44" />
                Tus datos están seguros. No spam.
              </p>
            </form>
          </div>
        </div>}
    </div>;
}